import React from "react";
import { formatToShortDate, formatUniversalNumber } from "../../../lib/utils";

interface DetailItem {
  label: string;
  value: string | number | null;
  type?: "id" | "amount" | "date" | "text";
  badge?: string;
}

interface InvoiceItem {
  refInvoiceId: string;
  invoiceAmount: number;
  redemptionTypeAccountName: string;
}

interface DetailsCardProps {
  title: string;
  details: DetailItem[];
  invoices?: InvoiceItem[];
  stringOverrides?: {
    invoiceTableHeaderLabel?: string;
    invoiceIdLabel?: string;
    invoiceAmountLabel?: string;
    redemptionTypeAccountLabel?: string;
    creditMemoTableHeaderLabel?: string;
    creditMemoIdLabel?: string;
    creditMemoAmountLabel?: string;
    creditMemoDateLabel?: string;
  };
}

const DetailsCard: React.FC<DetailsCardProps> = ({
  title,
  details,
  invoices,
  stringOverrides,
}) => {
  const renderValue = (item: DetailItem) => {
    switch (item.type) {
      case "id":
        return (
          <div className="text-base font-semibold BookEZ_font_color_gray_1 flex items-center">
            <span className="mr-2">{item.value}</span>
            {item.badge && (
              <span className="text-xs px-2 py-1 BookEZ_bg_blue_light BookEZ_font_color_blue rounded-full">
                {item.badge}
              </span>
            )}
          </div>
        );
      case "amount":
        return (
          <div
            className={`text-base font-semibold ${
              typeof item?.value === "number" && item?.value <= 0
                ? "BookEZ_font_color_red"
                : "BookEZ_font_color_green"
            }`}
          >
            {item?.value ? item?.value : "-"}
          </div>
        );
      case "date":
        return (
          <div className="text-base font-semibold BookEZ_font_color_gray_2">
            {item.value ? formatToShortDate(item.value.toString()) : "-"}
          </div>
        );
      default:
        return (
          <div className="text-base font-semibold BookEZ_font_color_gray_2">
            {item.value || "-"}
          </div>
        );
    }
  };

  return (
    <div className="BookEZ_card_bg BookEZ_card_radius border BookEZ_card_border_color shadow-sm overflow-hidden">
      <div className="BookEZ_transaction_header_bg px-5 py-3 border-b BookEZ_card_border_color">
        <h3 className="BookEZ_font_body_size BookEZ_font_body_medium BookEZ_font_color_gray_2">
          {title}
        </h3>
      </div>
      <div className="p-5">
        <div className="flex flex-wrap gap-6 justify-between mb-6">
          {details.map((item, index) => (
            <div
              key={index}
              className="BookEZ_transaction_card_bg p-4 BookEZ_card_radius flex-1 min-w-[200px]"
            >
              <div className="BookEZ_font_body_size BookEZ_font_color_gray_3 mb-1">
                {item.label}
              </div>
              {renderValue(item)}
            </div>
          ))}
        </div>

        {/* Invoice Table */}
        {invoices && invoices.length > 0 && (
          <div
            className="BookEZ_card_bg BookEZ_card_radius border BookEZ_card_border_color shadow-sm overflow-hidden"
            key={"invoices"}
          >
            <div className="BookEZ_transaction_header_bg px-5 py-3 border-b BookEZ_card_border_color">
              <h3 className="BookEZ_font_body_size BookEZ_font_body_medium BookEZ_font_color_gray_2">
                {stringOverrides?.invoiceTableHeaderLabel ||
                  stringOverrides?.creditMemoTableHeaderLabel}
              </h3>
            </div>

            <div className="grid grid-cols-3 BookEZ_transaction_header_bg p-4 border-b BookEZ_card_border_color">
              <div className="BookEZ_font_body_size BookEZ_font_body_medium BookEZ_font_color_gray_2">
                {stringOverrides?.invoiceIdLabel ||
                  stringOverrides?.creditMemoIdLabel}
              </div>
              <div className="BookEZ_font_body_size BookEZ_font_body_medium BookEZ_font_color_gray_2 text-right">
                {stringOverrides?.invoiceAmountLabel ||
                  stringOverrides?.creditMemoAmountLabel}
              </div>
              <div className="BookEZ_font_body_size BookEZ_font_body_medium BookEZ_font_color_gray_2 text-right">
                {stringOverrides?.redemptionTypeAccountLabel ||
                  stringOverrides?.creditMemoDateLabel}
              </div>
            </div>

            {invoices?.map((inv: any) => (
              <div
                className="grid grid-cols-3 p-4 border-b last:border-b-0 BookEZ_card_border_color hover:bg-[#fbfafa12] transition-colors"
                key={inv?.entryId}
              >
                <div className="BookEZ_font_body_size BookEZ_font_color_gray_2 BookEZ_font_body_medium truncate max-w-[200px]">
                  {inv.refInvoiceId}
                </div>
                <div className="BookEZ_font_color_gray_2 text-right">
                  <span
                    className={`inline-block BookEZ_bg_yellow_light BookEZ_font_color_yellow BookEZ_font_body_size BookEZ_font_body_medium px-3 py-1 rounded-full`}
                  >
                    {formatUniversalNumber(inv.invoiceAmount)}
                  </span>
                </div>
                <div className="BookEZ_font_color_gray_2 text-right">
                  {inv.redemptionTypeAccountName || "-"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailsCard;
